import { FunctionComponent } from "react";
import {
  styled,
  TextField as MUITextField,
  TextFieldProps,
  lighten,
  alpha,
} from "@mui/material";
import { InputSettings, OutlineColor } from "../../types/theme";
import { defaultAlphaConstant } from "../../../theme/defaultValues";
import { useCapitalization } from "../../builder/Theme";
import { deepMergeIgnoreUndefined } from "../../helpers/object";
import { BodySizes } from "../../builder/useFontsLayout";
import {
  INPUT_VERTICAL_PADDING,
  INPUT_PADDING_OFFSET,
  INPUT_SIZES,
  INPUT_HORIZONTAL_PADDING,
  INPUT_LABEL_POSITIONS,
} from "../../../theme/defaultConstants";
import { TextFieldLabel } from "./TextFieldLabel";

interface Props
  extends Pick<
    TextFieldProps,
    | "className"
    | "helperText"
    | "error"
    | "onChange"
    | "minRows"
    | "multiline"
    | "tabIndex"
    | "onKeyDown"
    | "label"
    | "inputProps"
    | "value"
    | "name"
    | "onBlur"
    | "ref"
    | "defaultValue"
    | "inputRef"
    | "InputProps"
    | "maxRows"
    | "type"
    | "fullWidth"
    | "placeholder"
    | "onInput"
  > {
  settingsOverride?: InputSettings;
  settingsOverrideColors?: OutlineColor;
  labelPosition?: "above" | "inline";
}

const StyledTextField = styled(MUITextField)<
  Pick<Props, "label" | "settingsOverride" | "settingsOverrideColors">
>(({ theme, label, settingsOverride, settingsOverrideColors }) => {
  const inputs = settingsOverride
    ? deepMergeIgnoreUndefined(theme.settings.inputs, settingsOverride)
    : theme.settings.inputs;
  const textTransform = useCapitalization(
    inputs?.capitalization === "Ag" ? undefined : inputs?.capitalization,
  );

  const minHeight = INPUT_SIZES[inputs.size] || INPUT_SIZES.medium;
  const labelPosition =
    INPUT_LABEL_POSITIONS[inputs.size] || INPUT_LABEL_POSITIONS.medium;

  const borderColor =
    settingsOverrideColors?.borderColor || theme.palette.inputsUnderline.main;

  const backgroundColor =
    settingsOverrideColors?.backgroundColor || theme.palette.inputs.default;

  const textColor =
    settingsOverrideColors?.textColor || theme.palette.text.inputDefault;

  return {
    "& .MuiInputBase-input::placeholder": {
      opacity: 0.6,
    },
    "& .MuiFormControl-root.MuiTextField-root": {
      marginBottom: "0rem !important",
    },
    "& .MuiFilledInput-root": {
      overflow: "hidden",
      padding: `0 !important`,
      fontFamily: theme.settings.fonts.body.fontFamily,
      borderStyle: "solid",
      fontSize:
        theme.typography[
          BodySizes[theme.settings.fonts.inputs?.size || "medium"]
        ].fontSize,
      borderWidth: inputs.border.thickness,
      borderRadius: inputs.border.radius * 5,
      borderColor: alpha(borderColor, inputs.border.opacity),
      backgroundColor,
      color: textColor,
      "&:before, &:after": {
        borderBottom: inputs.showUnderline ? "" : "none !important",
      },
      "&:hover": {
        backgroundColor,
      },
      "& input, & textarea": {
        textTransform,
        padding: `${
          INPUT_VERTICAL_PADDING - INPUT_PADDING_OFFSET
        }px ${INPUT_HORIZONTAL_PADDING}px ${INPUT_PADDING_OFFSET}px ${INPUT_HORIZONTAL_PADDING}px`,
        minHeight,
        ...(!label
          ? {
              paddingTop: INPUT_VERTICAL_PADDING / 2,
              paddingBottom: INPUT_VERTICAL_PADDING / 2,
            }
          : {}),
      },
    },
    "& .MuiFilledInput-root.Mui-focused": {
      backgroundColor,
    },
    "& .MuiInputLabel-root": {
      left: theme.spacing(0.6),
      fontFamily: theme.settings.fonts.body.fontFamily,
      transition: "0.2s",
      fontSize: `calc(${
        theme.typography[
          BodySizes[theme.settings.fonts.inputs?.size || "medium"]
        ].fontSize
      } * 1.05)`,
      color: lighten(textColor, defaultAlphaConstant.alphaLight),
      transform: `translate(12px, ${labelPosition}px) scale(0.75)`,
      "&:not(.MuiInputLabel-shrink)": {
        top: "50%",
        transform: "translate(12px, -50%) scale(1)",
      },
    },
    "& textarea": {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1.5),
    },
    "& .MuiInputLabel-shrink": {
      marginTop: "0rem",
    },
  };
});

export const Container = styled("div")<{ fullWidth?: boolean }>(
  ({ fullWidth }) => ({
    width: fullWidth ? "100%" : "initial",
    display: "flex",
    flexDirection: "column",
    "& .MuiFormHelperText-root": {
      paddingLeft: INPUT_HORIZONTAL_PADDING,
      marginLeft: 0,
      marginRight: 0,
    },
  }),
);

const TextField: FunctionComponent<Props> = ({
  className,
  error,
  label,
  helperText,
  fullWidth,
  onChange,
  type,
  onInput,
  onKeyDown,
  settingsOverride,
  settingsOverrideColors,
  placeholder,
  labelPosition,
  inputProps,
  InputProps,
  inputRef,
  tabIndex,
  multiline,
  minRows,
  maxRows,
  defaultValue,
  name,
  onBlur,
  ref,
  value,
}) => {
  const withErrors = !!error;
  const field = (
    <StyledTextField
      variant="filled"
      error={withErrors}
      label={labelPosition !== "above" ? label : undefined}
      placeholder={
        placeholder ||
        (labelPosition === "above" && typeof label === "string"
          ? label
          : undefined)
      }
      settingsOverride={settingsOverride}
      settingsOverrideColors={settingsOverrideColors}
      onChange={onChange}
      type={type}
      onInput={onInput}
      onKeyDown={onKeyDown}
      inputProps={inputProps}
      InputProps={InputProps}
      inputRef={inputRef}
      tabIndex={tabIndex}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      defaultValue={defaultValue}
      name={name}
      onBlur={onBlur}
      ref={ref}
      value={value}
      helperText={helperText}
    />
  );
  return (
    <Container fullWidth={fullWidth} className={className}>
      {labelPosition === "above" ? (
        <TextFieldLabel>
          {label}
          {field}
        </TextFieldLabel>
      ) : (
        field
      )}
    </Container>
  );
};

export default TextField;
